import type { Phase } from '@il-colpo/engine';
import type { Mode } from './composer.ts';

/** Where the music sits: one scene per kind of screen (ART_BIBLE §11.2). */
export type Scene = 'off' | 'menu' | 'planning' | 'job' | 'escape' | 'hideout' | 'reveal';

/** How loud each instrument is, 0–1, and the tempo and mode the band plays in. */
export interface LayerPlan {
  bpm: number;
  mode: Mode;
  /** Rain and room tone. */
  ambience: number;
  brushes: number;
  bass: number;
  organ: number;
  vibes: number;
  /** The electric piano of the menus. */
  piano: number;
  /** Ride, clusters and tremolo: the layer that only the alarm brings in. */
  tension: number;
  /** A busier brush pattern, for when the alarm is up. */
  busy: boolean;
}

export const BASE_BPM = 78;
/** The alarm speeds the band up by at most this much (ART_BIBLE §11.2). */
export const MAX_SPEEDUP = 0.3;

const SILENT: LayerPlan = {
  bpm: BASE_BPM,
  mode: 'minor',
  ambience: 0,
  brushes: 0,
  bass: 0,
  organ: 0,
  vibes: 0,
  piano: 0,
  tension: 0,
  busy: false,
};

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/** Rises from 0 at `from` to 1 at `to`. */
const ramp = (x: number, from: number, to: number): number => clamp01((x - from) / (to - from));

/**
 * What the band plays in a scene at an alarm level (0–1). The layers come in one by one as the
 * alarm climbs: brushes and bass, then the organ, then the tension, while the tempo rises by up
 * to 30 % and, from 60 %, the harmony turns to the dominant.
 */
export function layerPlan(scene: Scene, alarm: number): LayerPlan {
  const a = clamp01(alarm);
  const speed = 1 + MAX_SPEEDUP * a;
  const dark: Mode = a >= 0.6 ? 'phrygianDominant' : 'minor';
  switch (scene) {
    case 'off':
      return SILENT;
    case 'menu':
      return { ...SILENT, bpm: 72, piano: 1, bass: 0.8, vibes: 0.3 };
    case 'planning':
      return { ...SILENT, bpm: 70, brushes: 0.45, bass: 0.8, vibes: 0.6, piano: 0.5 };
    case 'job':
      return {
        bpm: BASE_BPM * speed,
        mode: dark,
        ambience: 1,
        brushes: 0.8,
        bass: 0.9,
        organ: ramp(a, 0.2, 0.45),
        vibes: 1 - ramp(a, 0.4, 0.7),
        piano: 0,
        tension: ramp(a, 0.45, 0.85),
        busy: a >= 0.45,
      };
    case 'escape':
      return {
        bpm: 92 * (1 + 0.15 * a),
        mode: 'phrygianDominant',
        ambience: 0.8,
        brushes: 1,
        bass: 1,
        organ: 0.7,
        vibes: 0,
        piano: 0,
        tension: 0.55 + 0.45 * a,
        busy: true,
      };
    case 'hideout':
      return { ...SILENT, bpm: 58, brushes: 0.35, bass: 0.7, organ: 0.45, vibes: 0.4 };
    case 'reveal':
      return { ...SILENT, bpm: 96, mode: 'minor', organ: 0.9, bass: 0.7, brushes: 0.4 };
  }
}

/** The scene a game phase belongs to. */
export function sceneForPhase(phase: Phase): Scene {
  switch (phase) {
    case 'briefing':
      return 'menu';
    case 'planning':
      return 'planning';
    case 'execution':
      return 'job';
    case 'escape':
      return 'escape';
    case 'hideout':
      return 'hideout';
    case 'reveal':
    case 'ended':
      return 'reveal';
  }
}

/** Gain (0–1) to decibels, with silence at −80 dB. */
export function gainToDb(gain: number): number {
  return gain <= 0.0001 ? -80 : Math.max(-80, 20 * Math.log10(gain));
}
